import { ReactElement, Fragment } from "react";
import { getMiddle, IDimensions } from "./chartUtils";
import * as d3 from "d3";
import { TickTypes } from "./types";
import { useTheme } from "@mui/material";

interface IAxisHorizontalProps {
  chartID: string;
  dimensions: IDimensions;
  label?: string;
  scale: d3.ScaleLinear<number, number> | d3.ScaleBand<string>;
  ticks: TickTypes;
  fontSize?: string | number;
  ticksAngle?: number;
  formatTick?: (n: number | { value: number, } | string, index: number, length: number) => string;
  marginTop?: number;
  subTicks?: (x: number, y: number) => ReactElement;
}

const tickTop = 15;

const AxisHorizontal = ({ chartID, dimensions, label, formatTick, scale, ticks, ticksAngle, fontSize, marginTop, subTicks }: IAxisHorizontalProps): ReactElement => {
  const theme = useTheme();
  const barID = `${chartID}-axis-horizontal`;
  const bandwidth = "bandwidth" in scale ? getMiddle((scale as d3.ScaleBand<string>).bandwidth()) : 0;

  return (
    <g className="Axis AxisHorizontal" transform={`translate(0, ${dimensions.boundedHeight})`}>
      <line className="Axis__line" x2={dimensions.boundedWidth}/>

      {ticks.map((tick: any, i) => {
        const x = scale(tick) ?? 0;
        const tx = bandwidth > 0 ? x + bandwidth : x;
        let textAnchor:  "end" | "start" | "middle" = i === 0 && bandwidth === 0 ? "start" : i === ticks.length - 1 && bandwidth === 0 ? "end" : "middle";
        if (ticksAngle) {
          textAnchor = "end";
        }

        return (
          <Fragment key={`${barID}-tick-${i}`}>
            <text
              fill={theme.palette.text.primary}
              className="Axis__tick"
              x={0}
              y={marginTop ?? 0}
              textAnchor={textAnchor}
              dominantBaseline="middle"
              fontSize={fontSize}
              transform={`translate(${Math.round(tx)}, ${Math.round(tickTop)}) ${ticksAngle ? `rotate(-${ticksAngle})` : ""}`}>
              {formatTick && typeof tick === "number" ? formatTick(tick as number, i, ticks.length) : tick}
            </text>

            {subTicks && subTicks(x, 0)}

            <line className="Axis__line" y1={-1} y2={5} x1={x} x2={x}/>
          </Fragment>
        );
      })}

      <line className="Axis__line" y1={-1} y2={5} x1={dimensions.boundedWidth} x2={dimensions.boundedWidth}/>

      {label &&
        <text className="Axis__label" textAnchor="middle" dominantBaseline="auto"
          x={getMiddle(dimensions.boundedWidth)}
          y={dimensions.marginBottom - 5} fill={theme.palette.text.primary} fontWeight="bold" fontSize="1em">
          {label}
        </text>
      }
    </g>
  );
};

export default AxisHorizontal;
